import { supabase } from '../config/supabaseClient';

/**
 * Wallet Analytics Service
 * Provides functions to fetch wallet analytics for admin dashboard
 */

/**
 * Get system-wide wallet statistics
 * @returns {Promise<Object>} System-wide wallet stats
 */
export const getSystemWalletStats = async () => {
    try {
        console.log('🔍 Fetching system wallet stats...');
        
        const { data, error } = await supabase
            .rpc('get_system_wallet_stats');

        if (error) {
            console.error('❌ RPC Error:', error);
            throw error;
        }

        console.log('✅ Raw RPC Response:', data);

        // Return first row (function returns table with one row)
        const result = {
            success: true,
            data: data && data.length > 0 ? data[0] : null
        };
        
        console.log('📊 Parsed Stats:', result.data);
        
        return result;
    } catch (error) {
        console.error('❌ Error fetching system wallet stats:', error);
        console.error('Error details:', {
            message: error.message,
            hint: error.hint,
            details: error.details,
            code: error.code
        });
        return {
            success: false,
            error: error.message,
            fullError: error
        };
    }
};

/**
 * Get wallet analytics for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User wallet analytics
 */
export const getUserWalletAnalytics = async (userId) => {
    try {
        const { data, error } = await supabase
            .rpc('get_user_wallet_analytics', {
                target_user_id: userId
            });

        if (error) throw error;

        return {
            success: true,
            data: data && data.length > 0 ? data[0] : null
        };
    } catch (error) {
        console.error('Error fetching user wallet analytics:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get wallet summary for all users
 * @returns {Promise<Object>} Array of user wallet summaries
 */
export const getAllUsersWalletSummary = async () => {
    try {
        const { data, error } = await supabase
            .rpc('get_all_users_wallet_summary');

        if (error) throw error;

        return {
            success: true,
            data: data || []
        };
    } catch (error) {
        console.error('Error fetching all users wallet summary:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get transaction statistics by date
 * @param {number} daysBack - Number of days to look back (default: 7)
 * @returns {Promise<Object>} Daily transaction statistics
 */
export const getTransactionStatsByDate = async (daysBack = 7) => {
    try {
        const { data, error } = await supabase
            .rpc('get_transaction_stats_by_date', {
                days_back: daysBack
            });

        if (error) throw error;

        // Transform data for chart display
        const transformedData = (data || []).map(row => ({
            date: new Date(row.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            deposits: row.deposits_count || 0,
            withdrawals: row.withdrawals_count || 0,
            transfers: row.transfers_count || 0,
            depositsVolume: parseFloat(row.deposits_volume_usd || 0),
            withdrawalsVolume: parseFloat(row.withdrawals_volume_usd || 0),
            transfersVolume: parseFloat(row.transfers_volume_usd || 0),
            totalVolume: parseFloat(row.total_volume_usd || 0)
        })).reverse(); // Reverse to show oldest first (left to right)

        return {
            success: true,
            data: transformedData
        };
    } catch (error) {
        console.error('Error fetching transaction stats by date:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get top users by wallet activity
 * @param {number} limit - Number of users to return (default: 10)
 * @returns {Promise<Object>} Top active users
 */
export const getTopUsersByActivity = async (limit = 10) => {
    try {
        const { data, error } = await supabase
            .rpc('get_top_users_by_activity', {
                limit_count: limit
            });

        if (error) throw error;

        return {
            success: true,
            data: data || []
        };
    } catch (error) {
        console.error('Error fetching top users by activity:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get system-wide token summary (all supported tokens, total balances)
 * Used in admin dashboard to show multi-token holdings breakdown.
 */
export const getAllTokenSummary = async () => {
    try {
        const { data, error } = await supabase.rpc('get_all_token_summary');
        if (error) throw error;
        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching token summary:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get per-token balances for a specific user (admin use)
 */
export const getUserTokenBalances = async (userId) => {
    try {
        const { data, error } = await supabase.rpc('get_user_token_balances', { p_user_id: userId });
        if (error) throw error;
        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching user token balances:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Format ETH balance with proper decimals
 * @param {number|string} ethAmount - ETH amount
 * @param {number} decimals - Number of decimal places (default: 4)
 * @returns {string} Formatted ETH amount
 */
export const formatETH = (ethAmount, decimals = 4) => {
    if (!ethAmount || isNaN(ethAmount)) return '0.0000';
    return parseFloat(ethAmount).toFixed(decimals);
};

/**
 * Format USD amount
 * @param {number|string} usdAmount - USD amount
 * @param {boolean} includeSymbol - Include $ symbol (default: true)
 * @returns {string} Formatted USD amount
 */
export const formatUSD = (usdAmount, includeSymbol = true) => {
    if (!usdAmount || isNaN(usdAmount)) return includeSymbol ? '$0.00' : '0.00';
    const formatted = parseFloat(usdAmount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    return includeSymbol ? `$${formatted}` : formatted;
};

/**
 * Format large numbers with K/M suffixes
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatLargeNumber = (num) => {
    if (!num || isNaN(num)) return '0';
    
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
};

/**
 * Get time ago text
 * @param {string|Date} date - Date to compare
 * @returns {string} Time ago text (e.g., "2 hours ago")
 */
export const getTimeAgo = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return past.toLocaleDateString();
};
